import React from 'react';

/**
 * Unified loading spinner — replaces ClipLoader and all ad-hoc loading states.
 *
 * @param {'sm'|'md'|'lg'} size        Spinner diameter (default 'md')
 * @param {'spinner'|'dots'|'pulse'}  variant  Animation style (default 'spinner')
 * @param {string}  message   Optional text below the spinner
 * @param {boolean} fullScreen  Centre within the full viewport
 * @param {string}  className   Extra classes on the wrapper
 */

const SIZE_MAP = { sm: 'w-5 h-5', md: 'w-7 h-7', lg: 'w-10 h-10' };
const DOT_SIZE = { sm: 'w-1.5 h-1.5', md: 'w-2 h-2', lg: 'w-2.5 h-2.5' };

const Spinner = ({ size = 'md' }) => (
  <div className={`${SIZE_MAP[size]} rounded-full border-2 border-primary/30 border-t-primary animate-spin`} />
);

const Dots = ({ size = 'md' }) => (
  <div className="flex items-center gap-1.5">
    {[0, 1, 2].map(i => (
      <div
        key={i}
        className={`${DOT_SIZE[size]} rounded-full bg-primary`}
        style={{
          animation: 'dotBounce 1.4s ease-in-out infinite',
          animationDelay: `${i * 0.16}s`,
        }}
      />
    ))}
  </div>
);

const Pulse = ({ size = 'md' }) => (
  <div className="relative flex items-center justify-center">
    <div className={`${SIZE_MAP[size]} rounded-full bg-primary/20 animate-ping absolute`} />
    <div className={`${SIZE_MAP[size]} rounded-full bg-primary/40`} />
  </div>
);

const VARIANTS = { spinner: Spinner, dots: Dots, pulse: Pulse };

const LoadingSpinner = ({
  size = 'md',
  variant = 'spinner',
  message,
  fullScreen = false,
  className = '',
}) => {
  const Indicator = VARIANTS[variant] || Spinner;

  const content = (
    <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
      <Indicator size={size} />
      {message && <p className="text-sm text-text-muted animate-pulse-slow">{message}</p>}
    </div>
  );

  if (fullScreen) {
    return <div className="flex items-center justify-center min-h-screen bg-bg">{content}</div>;
  }
  return <div className="flex items-center justify-center min-h-[200px]">{content}</div>;
};

export default LoadingSpinner;
